/**
 * Standalone Edit Client dialog — opens inline on any page (e.g. the Client
 * detail page) without requiring a redirect to the Clients list. Mirrors the
 * form fields/UX from `pages/ClientsPage.js`'s embedded dialog.
 *
 * Usage:
 *   <EditClientDialog
 *     clientId={id}
 *     open={open}
 *     onOpenChange={setOpen}
 *     onSaved={(updated) => { /* refresh local state * / }}
 *   />
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'prospect', label: 'Prospect' },
];

const SOURCE_OPTIONS = [
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'exhibition', label: 'Exhibition' },
  { value: 'other', label: 'Other' },
];

const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam',
  'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir',
  'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha',
  'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

const EMPTY = {
  company_name: '', contact_person: '', email: '', phone: '', website: '',
  address: '', city: '', state: '', country: 'India', pincode: '',
  gst: '', industry: '', source: 'other', status: 'active', notes: '',
  billing_emails: [],
};

export default function EditClientDialog({ clientId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  // Fetch client whenever the dialog opens for a given id.
  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}`)
      .then(r => {
        const c = r.data?.data || r.data || {};
        setFormData({
          company_name: c.company_name || '',
          contact_person: c.contact_person || '',
          email: c.email || '',
          phone: c.phone || '',
          website: c.website || '',
          address: c.address || c.company_address || '',
          city: c.city || '',
          state: c.state || '',
          country: c.country || 'India',
          pincode: c.pincode || '',
          gstin: c.gstin || c.gst_number || c.gst || '',
          industry: c.industry || '',
          source: c.source || 'other',
          status: c.status || 'active',
          notes: c.notes || '',
          billing_emails: Array.isArray(c.billing_emails) ? c.billing_emails.map(b => ({ ...b })) : [],
        });
      })
      .catch(() => toast.error('Failed to load client'))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  const updateField = (key, value) => setFormData(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    try {
      const res = await api.put(`/clients/${clientId}`, formData);
      const updated = res.data?.data || res.data;
      toast.success('Client updated');
      onOpenChange(false);
      if (onSaved) onSaved(updated);
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const addBillingEmail = () => updateField(
    'billing_emails',
    [...(formData.billing_emails || []), { email: '', role: 'to', label: '' }],
  );

  const updateBillingEmail = (idx, patch) => {
    const arr = [...formData.billing_emails];
    arr[idx] = { ...arr[idx], ...patch };
    updateField('billing_emails', arr);
  };

  const removeBillingEmail = (idx) => {
    updateField('billing_emails', formData.billing_emails.filter((_, i) => i !== idx));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle>Edit Client</DialogTitle>
          <DialogDescription>Update client information</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading…</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company Name *" id="company_name">
                <Input id="company_name" value={formData.company_name}
                  onChange={(e) => updateField('company_name', e.target.value)}
                  placeholder="Enter company name" required data-testid="client-company-name" />
              </Field>
              <Field label="Contact Person" id="contact_person">
                <Input id="contact_person" value={formData.contact_person}
                  onChange={(e) => updateField('contact_person', e.target.value)}
                  placeholder="Primary contact name" data-testid="client-contact-person" />
              </Field>
              <Field label="Email" id="email">
                <Input id="email" type="email" value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="client@example.com" data-testid="client-email" />
              </Field>
              <Field label="Phone" id="phone">
                <Input id="phone" value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+91 98765 43210" data-testid="client-phone" />
              </Field>
              <Field label="Website" id="website">
                <Input id="website" value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="https://example.com" />
              </Field>
              <Field label="City" id="city">
                <Input id="city" value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)} placeholder="Mumbai" />
              </Field>
              <Field label="State" id="state">
                <Select
                  value={formData.state || '_none'}
                  onValueChange={(v) => updateField('state', v === '_none' ? '' : v)}
                >
                  <SelectTrigger data-testid="client-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Select state</SelectItem>
                    {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source" id="source">
                <Select value={formData.source} onValueChange={(v) => updateField('source', v)}>
                  <SelectTrigger data-testid="client-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status" id="status">
                <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
                  <SelectTrigger data-testid="client-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="GST Number" id="gstin">
                <Input id="gstin" value={formData.gstin}
                  onChange={(e) => updateField('gstin', e.target.value)}
                  placeholder="e.g. 27AABCT1234A1Z5"
                  data-testid="client-gstin" />
              </Field>
              <Field label="Industry" id="industry">
                <Input id="industry" value={formData.industry}
                  onChange={(e) => updateField('industry', e.target.value)}
                  placeholder="e.g., Technology, Retail" />
              </Field>
            </div>

            <Field label="Address" id="address">
              <Input id="address" value={formData.address}
                onChange={(e) => updateField('address', e.target.value)} placeholder="Full address" />
            </Field>

            {/* Billing emails */}
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Billing Emails</Label>
                  <p className="text-xs text-muted-foreground">
                    Tax invoices and payment receipts will be sent to these addresses.
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline"
                  onClick={addBillingEmail} data-testid="add-billing-email">+ Add</Button>
              </div>
              {(formData.billing_emails || []).length === 0 ? (
                <p className="text-xs text-amber-500 italic">
                  No billing emails added — <strong>tax invoices will NOT be emailed</strong> for this client until at least one billing email is added.
                </p>
              ) : formData.billing_emails.map((be, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-5" type="email" placeholder="email@client.com"
                    value={be.email || ''}
                    onChange={(e) => updateBillingEmail(idx, { email: e.target.value })}
                    data-testid={`billing-email-${idx}`} />
                  <Select value={be.role || 'to'}
                    onValueChange={(v) => updateBillingEmail(idx, { role: v })}>
                    <SelectTrigger className="col-span-2" data-testid={`billing-role-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="to">TO</SelectItem>
                      <SelectItem value="cc">CC</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="col-span-4" placeholder="Label (e.g. Accounts)"
                    value={be.label || ''}
                    onChange={(e) => updateBillingEmail(idx, { label: e.target.value })} />
                  <Button type="button" size="sm" variant="ghost" className="col-span-1"
                    onClick={() => removeBillingEmail(idx)}
                    data-testid={`remove-billing-email-${idx}`}>✕</Button>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="client-submit-btn">
                {saving ? 'Saving…' : 'Update Client'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, id, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);
